/**
 * The team: who is on the roster, what they specialise in, and what they carry.
 *
 * This page exists because of the hole the workbook left. Eighteen owner slots came
 * across the migration null and the roadmap can only report that fact - it cannot
 * tell you who could plausibly fill one, because a Gantt chart has no idea that Ha
 * does front-end and Meherzad does infrastructure. That is the question this page
 * answers, so the two views are complementary rather than duplicates.
 *
 * It reads /api/people/workload rather than /api/people. The distinguishing question
 * is "who is loaded and who is free", and that is only visible once ownership is
 * aggregated per person instead of per project.
 *
 * The roadmap is fetched too, with archived lanes included, purely to turn project
 * ids into names. Included rather than filtered because a person can perfectly well
 * be the DRI of an archived project, and an id that fails to resolve would render as
 * a bare uuid next to nine real names.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import PersonEditor from '../components/PersonEditor';
import { describeError, getRoadmap, getSkills, getWorkload } from '../services/api';
import { palette, radius } from '../styles/theme';
import {
  Chip,
  ErrorText,
  Hint,
  Panel,
  PrimaryButton,
  SecondaryButton,
  ToggleButton,
} from '../styles/ui';
import type { Person, PersonWorkload, SkillInfo } from '../types';

const Summary = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Spacer = styled.div`
  flex: 1;
`;

const Status = styled.p`
  margin: 0;
  color: ${palette.inkSoft};
`;

const Roster = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Row = styled.li<{ $inactive: boolean }>`
  border: 1px solid ${palette.border};
  border-radius: ${radius.md};
  background: ${(p) => (p.$inactive ? '#FBF6F9' : palette.card)};
  /* Deactivated people stay legible rather than being faded to grey. They are still
     the recorded owner of real work, and the roadmap will keep showing their name. */
  opacity: ${(p) => (p.$inactive ? 0.75 : 1)};
  overflow: hidden;
`;

/*
  A grid rather than a <table>. The cells wrap to one column on a narrow screen, and
  a table cannot reflow - it would either scroll sideways or squeeze the skill chips
  into a column two words wide.
*/
const Head = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, 1.1fr) minmax(220px, 1.6fr) minmax(150px, 0.9fr) auto;
  gap: 10px 16px;
  align-items: center;
  padding: 12px 14px;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Who = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const Name = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${palette.ink};
`;

const Email = styled.span`
  font-size: 12px;
  color: ${palette.inkSoft};
  overflow-wrap: anywhere;
`;

const Cell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  min-width: 0;
`;

/**
 * A skill, coloured by strength.
 *
 * Level is carried by fill AND by the word in the tooltip, and primary additionally
 * carries a dot. Nothing about "who to ask" should depend on distinguishing pink
 * from warm grey.
 */
const SkillChip = styled.span<{ $primary: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  border-radius: ${radius.pill};
  padding: 2px 9px;
  white-space: nowrap;
  color: ${(p) => (p.$primary ? '#ffffff' : palette.ink)};
  background: ${(p) => (p.$primary ? palette.hotPink : palette.blush)};
  border: 1px solid ${(p) => (p.$primary ? palette.hotPink : palette.borderStrong)};

  &::before {
    content: ${(p) => (p.$primary ? "'●'" : "'○'")};
    font-size: 8px;
    line-height: 1;
  }
`;

/** Somebody with no skills recorded and no work. The thing this page is here to find. */
const Idle = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${palette.warning};
`;

const Nothing = styled.span`
  font-size: 12px;
  color: ${palette.inkSoft};
`;

const EditorPanel = styled.div`
  border-top: 1px solid ${palette.border};
  background: ${palette.blush};
  padding: 14px 16px 16px;
`;

const NewPanel = styled(Panel)`
  border-color: ${palette.borderStrong};
  margin-bottom: 12px;
`;

const PanelTitle = styled.h2`
  font-size: 15px;
  color: ${palette.deepMagenta};
  margin-bottom: 10px;
`;

export default function TeamPage() {
  const [people, setPeople] = useState<PersonWorkload[] | null>(null);
  const [skills, setSkills] = useState<SkillInfo[]>([]);
  const [projectNames, setProjectNames] = useState<ReadonlyMap<string, string>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [onlyIdle, setOnlyIdle] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // In parallel: none of the three depends on another, and serially this is three
      // Lambda round trips before the first name appears.
      const [workload, vocabulary, roadmap] = await Promise.all([
        getWorkload(),
        getSkills(),
        getRoadmap(true),
      ]);
      setPeople(workload);
      setSkills(vocabulary);
      setProjectNames(new Map(roadmap.projects.map((p) => [p.project_id, p.name])));
    } catch (err) {
      setError(describeError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Fold a saved person back into the list.
   *
   * The response is a Person and carries no workload counts, so the existing row's
   * counts are kept rather than defaulted to zero - editing somebody's name must not
   * make it look as though they just lost four projects. A new person genuinely owns
   * nothing, so zeroes are correct there.
   */
  const onSaved = useCallback((saved: Person) => {
    setPeople((current) => {
      const list = current ?? [];
      const existing = list.find((p) => p.email === saved.email);
      const merged: PersonWorkload = {
        ...saved,
        dri_project_ids: existing?.dri_project_ids ?? [],
        support_project_ids: existing?.support_project_ids ?? [],
        owned_phase_count: existing?.owned_phase_count ?? 0,
      };
      return existing
        ? list.map((p) => (p.email === saved.email ? merged : p))
        : [...list, merged];
    });
    setEditing(null);
    setAdding(false);
  }, []);

  const labels = useMemo(
    () => new Map(skills.map((s) => [s.skill, s.label])),
    [skills]
  );

  const sorted = useMemo(() => {
    const list = people ?? [];
    // Active first, then by name. Deactivated people sinking to the bottom means the
    // list reads as the current team without having to filter them out entirely.
    return [...list].sort(
      (a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name)
    );
  }, [people]);

  const isIdle = useCallback(
    (person: PersonWorkload) =>
      person.dri_project_ids.length === 0 &&
      person.support_project_ids.length === 0 &&
      person.owned_phase_count === 0,
    []
  );

  const visible = useMemo(
    () =>
      sorted.filter(
        (p) => (showInactive || p.active) && (!onlyIdle || (p.active && isIdle(p)))
      ),
    [sorted, showInactive, onlyIdle, isIdle]
  );

  const idleCount = sorted.filter((p) => p.active && isIdle(p)).length;
  const noSkillsCount = sorted.filter((p) => p.active && p.specialisations.length === 0).length;
  const activeCount = sorted.filter((p) => p.active).length;

  /** Project ids rendered as names, with the unresolvable id shown rather than hidden. */
  const projectList = (ids: string[]) =>
    ids.map((id) => projectNames.get(id) ?? id).sort((a, b) => a.localeCompare(b));

  return (
    <>
      <Summary>
        <Chip>{activeCount} on the roster</Chip>
        {idleCount ? <Chip>{idleCount} carrying nothing</Chip> : null}
        {noSkillsCount ? <Chip>{noSkillsCount} with no skills recorded</Chip> : null}
      </Summary>

      <Toolbar>
        <PrimaryButton
          type="button"
          onClick={() => {
            setAdding((v) => !v);
            setEditing(null);
          }}
          disabled={skills.length === 0}
        >
          {adding ? 'Close' : 'Add person'}
        </PrimaryButton>
        <ToggleButton
          type="button"
          $on={onlyIdle}
          aria-pressed={onlyIdle}
          onClick={() => setOnlyIdle((v) => !v)}
        >
          Carrying nothing{idleCount ? ` (${idleCount})` : ''}
        </ToggleButton>
        <ToggleButton
          type="button"
          $on={showInactive}
          aria-pressed={showInactive}
          onClick={() => setShowInactive((v) => !v)}
        >
          Show deactivated
        </ToggleButton>
        <SecondaryButton type="button" onClick={() => void load()} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </SecondaryButton>
        <Spacer />
        <Hint>Adding somebody here does not grant them a login.</Hint>
      </Toolbar>

      {error ? <ErrorText role="alert">{error}</ErrorText> : null}

      {adding ? (
        <NewPanel>
          <PanelTitle>Add somebody to the roster</PanelTitle>
          <PersonEditor
            person={null}
            skills={skills}
            people={sorted}
            onSaved={onSaved}
            onCancel={() => setAdding(false)}
          />
        </NewPanel>
      ) : null}

      <Panel>
        {loading && !people ? (
          <Status>Loading the team…</Status>
        ) : visible.length === 0 ? (
          <Status>
            {onlyIdle
              ? 'Everybody on the roster is carrying something.'
              : 'Nobody on the roster yet.'}
          </Status>
        ) : (
          <Roster>
            {visible.map((person) => {
              const dri = projectList(person.dri_project_ids);
              const support = projectList(person.support_project_ids);
              const open = editing === person.email;
              return (
                <Row key={person.email} $inactive={!person.active}>
                  <Head>
                    <Who>
                      <Name>
                        {person.name}
                        {person.active ? '' : ' (deactivated)'}
                      </Name>
                      <Email>{person.email}</Email>
                    </Who>

                    <Cell>
                      {person.specialisations.length === 0 ? (
                        <Nothing>No specialisations recorded</Nothing>
                      ) : (
                        [...person.specialisations]
                          // Primary first: the reason to scan this column is to find
                          // who to ask, not to read an alphabetical list.
                          .sort(
                            (a, b) =>
                              Number(b.level === 'primary') - Number(a.level === 'primary') ||
                              a.skill.localeCompare(b.skill)
                          )
                          .map((s) => (
                            <SkillChip
                              key={s.skill}
                              $primary={s.level === 'primary'}
                              title={
                                s.level === 'primary'
                                  ? 'The obvious person to ask'
                                  : 'Could cover this'
                              }
                            >
                              {labels.get(s.skill) ?? s.skill}
                            </SkillChip>
                          ))
                      )}
                    </Cell>

                    <Cell>
                      {dri.length === 0 && support.length === 0 && person.owned_phase_count === 0 ? (
                        <Idle>Carrying nothing</Idle>
                      ) : (
                        <>
                          {dri.length ? (
                            <Chip title={`DRI: ${dri.join(', ')}`}>DRI ×{dri.length}</Chip>
                          ) : null}
                          {support.length ? (
                            <Chip title={`Support: ${support.join(', ')}`}>
                              Support ×{support.length}
                            </Chip>
                          ) : null}
                          {person.owned_phase_count ? (
                            <Chip title="Includes ongoing Maintenance bands">
                              {person.owned_phase_count}{' '}
                              {person.owned_phase_count === 1 ? 'phase' : 'phases'}
                            </Chip>
                          ) : null}
                        </>
                      )}
                    </Cell>

                    <Cell>
                      <SecondaryButton
                        type="button"
                        onClick={() => {
                          setEditing(open ? null : person.email);
                          setAdding(false);
                        }}
                        aria-expanded={open}
                      >
                        {open ? 'Close' : 'Edit'}
                      </SecondaryButton>
                    </Cell>
                  </Head>

                  {open ? (
                    <EditorPanel>
                      <PersonEditor
                        person={person}
                        skills={skills}
                        people={sorted}
                        onSaved={onSaved}
                        onCancel={() => setEditing(null)}
                      />
                    </EditorPanel>
                  ) : null}
                </Row>
              );
            })}
          </Roster>
        )}
      </Panel>
    </>
  );
}
